import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Alert } from '../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../CommonComponents/OPL/IconButton/IconButton';
import { Modal } from '../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../CommonComponents/OPL/Modal/ModalHeader';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import {
  HeldAwayInstitution,
  HELD_AWAY_INSTITUTIONS_QUERY_KEY,
} from '../api/useDataReconciliationQuery';
import { useDeleteInstitutionMutation } from '../api/useDeleteInstitutionMutation';
import { DataReconciliationTableHeader } from './components/DataReconciliationTableHeader';
import { DataReconciliationTableStatus } from './components/DataReconciliationTableStatus';
import { DataReconciliationTableEmpty } from './DataReconciliationTable.empty';
import { HtmlTable } from '../../CommonComponents/OPL/HtmlTable/HtmlTable';

interface Props {
  clientId: string;
  institutions: HeldAwayInstitution[];
}

export const DataReconciliationTable: React.FC<Props> = ({ clientId, institutions }) => {
  const [linkToDelete, setLinkToDelete] = useState<Pick<
    HeldAwayInstitution,
    'itemId' | 'institutionName'
  > | null>(null);

  const {
    mutate: deleteInstitution,
    isLoading: isDeletingInstitution,
    error: deleteInstitutionError,
    reset: resetDeleteInstitutionMutation,
  } = useDeleteInstitutionMutation();

  const queryClient = useQueryClient();

  const onRequestConfirmationModalClose = useCallback(() => {
    resetDeleteInstitutionMutation();
    setLinkToDelete(null);
  }, [resetDeleteInstitutionMutation]);

  if (institutions.length === 0) {
    return <DataReconciliationTableEmpty />;
  }

  return (
    <>
      <HtmlTable variant="embedded">
        <table>
          <DataReconciliationTableHeader />
          <tbody>
            {institutions.map(({ itemId, institutionName, status, lastUpdated }) => (
              <tr key={itemId}>
                <td>
                  <Text fontWeight={500} isTruncated maxWidth={500}>
                    {institutionName}
                  </Text>
                </td>

                <td>
                  <Text fontWeight={500}>
                    {lastUpdated ? moment.utc(lastUpdated).fromNow() : ''}
                  </Text>
                </td>

                <td>
                  <DataReconciliationTableStatus status={status} />
                </td>

                <td>
                  <IconButton
                    aria-label={`Unlink ${institutionName}`}
                    icon="far fa-trash-alt"
                    variant="subtleInactive"
                    onClick={() => setLinkToDelete({ itemId, institutionName })}
                    ml="4"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HtmlTable>

      {!!linkToDelete && (
        <Modal isOpen onRequestClose={onRequestConfirmationModalClose} size="medium">
          <ModalHeader variant="warning">Warning</ModalHeader>
          <ModalCloseButton
            onClick={onRequestConfirmationModalClose}
            disabled={isDeletingInstitution}
          />

          {deleteInstitutionError && (
            <Alert
              message={deleteInstitutionError.message}
              variant="error"
              mx="24"
              mt="0"
              mb="16"
            />
          )}

          <ModalBody>
            Are you sure you want to unlink your <b>{linkToDelete.institutionName}</b> accounts?
            <br />
            Any associated accounts will also be deleted.
          </ModalBody>

          <ModalFooter>
            <Box display="flex" justifyContent="flex-end">
              <Button
                onClick={onRequestConfirmationModalClose}
                variant="subtleInactive"
                disabled={isDeletingInstitution}
              >
                Cancel
              </Button>

              <Button
                ml="12"
                variant="error"
                isLoading={isDeletingInstitution}
                onClick={() =>
                  deleteInstitution(
                    { clientId, itemId: linkToDelete?.itemId },
                    {
                      onSuccess() {
                        setLinkToDelete(null);
                        return queryClient.invalidateQueries(HELD_AWAY_INSTITUTIONS_QUERY_KEY);
                      },
                    },
                  )
                }
              >
                Remove Accounts
              </Button>
            </Box>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
